import { CLASS_NAME } from 'constants/class-names'
import { ATTR_NAME } from 'constants/attr-name'
import { Nullable } from 'constants/types'
import { isDomReady } from 'utils/dom'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { logger } from 'utils/system/logger'
interface IImageService {
    lazyloadImages: HTMLElement[]
    container: Nullable<HTMLElement>
    imageCallback(entries: IEntry[]): void
}
interface IEntry {
    isIntersecting: boolean
    target: HTMLElement
}
export class ImageService extends ComponentBase implements IImageService {
    private observer: IntersectionObserver
    public constructor() {
        super()
        this.observeImages(this.lazyloadImages).catch((e) => logger('error', e))
    }
    get lazyloadImages(): HTMLElement[] {
        return this.getElements(`.${CLASS_NAME.imageService.lazyload}`)
    }
    get container(): Nullable<HTMLElement> {
        return this.getElement(`#${ATTR_NAME.imageService.id}`)
    }
    public imageCallback(entries: IEntry[]): IEntry[] {
        for (const entry of entries) {
            if (entry.isIntersecting && entry.target) {
                const img = entry.target as HTMLImageElement
                const pictureEl =
                    img.parentElement?.tagName === 'PICTURE'
                        ? img.parentElement
                        : undefined
                const imgSrc = img.dataset.src
                const sources = pictureEl
                    ? this.getElements<HTMLSourceElement>('source', pictureEl)
                    : []
                if (imgSrc) {
                    if (pictureEl) {
                        this.updateSourceSrcSet(sources)
                    } else {
                        const preload = new Image()
                        preload.onload = (): void => {
                            this.updateImgSrcSet(img)
                        }
                        preload.src = imgSrc
                    }
                }
                // once the image has been loaded, drop the observer
                this.observer.unobserve(img)
            }
        }
        return entries
    }
    public updateImgSrcSet(img: HTMLImageElement): void {
        const imgSrc = img.dataset.src
        const imgSrcset = img.dataset.srcset
        if (imgSrcset) {
            img.srcset = imgSrcset
        }
        if (imgSrc) {
            img.src = imgSrc
        }
        img.classList.remove(`${CLASS_NAME.imageService.lazyload}`)
        img.classList.add(`${CLASS_NAME.imageService.lazyloaded}`)
    }
    public updateSourceSrcSet(sources: false | HTMLSourceElement[]): void {
        if (!sources) {
            return
        }
        sources.forEach((childEl) => {
            const sourceSrcset = childEl.dataset.srcset
            if (sourceSrcset) {
                childEl.srcset = sourceSrcset
            }
        })
    }
    /**
     * Load all of the images exposed to the DOM
     *
     * @param lazyloadImages
     */
    private async observeImages(lazyloadImages: HTMLElement[]): Promise<void> {
        await isDomReady()
        this.observer = new IntersectionObserver(
            this.imageCallback.bind(this),
            {
                root: this.container,
                rootMargin: '0px 0px 200px 0px'
            }
        )
        for (const image of lazyloadImages) {
            this.observer.observe(image)
        }
    }
}
